import React from 'react'
import {
	InsertDriveFile,
	NotificationsActive,
	Assignment,
	Key,
	People,
	Security,
} from '@mui/icons-material'
import {
	InboxItem,
	InboxNotificationCategory,
	InboxNotificationObjectType,
} from '../../api/types/inbox.interfaces'

// Function to get category icon
export const getCategoryIcon = (category: InboxNotificationCategory) => {
	switch (category) {
		case InboxNotificationCategory.File:
			return (
				<InsertDriveFile className="w-5 h-5 fill-text-table dark:fill-text-table-dark" />
			)
		case InboxNotificationCategory.Event:
			return (
				<NotificationsActive className="w-5 h-5 fill-text-table dark:fill-text-table-dark" />
			)
		case InboxNotificationCategory.Task:
			return (
				<Assignment className="w-5 h-5 fill-text-table dark:fill-text-table-dark" />
			)
		case InboxNotificationCategory.Access:
			return (
				<Key className="w-5 h-5 fill-text-table dark:fill-text-table-dark" />
			)
		case InboxNotificationCategory.Team:
			return (
				<People className="w-5 h-5 fill-text-table dark:fill-text-table-dark" />
			)
		case InboxNotificationCategory.Security:
			return (
				<Security className="w-5 h-5 fill-text-table dark:fill-text-table-dark" />
			)
		default:
			return (
				<NotificationsActive className="w-5 h-5 fill-text-table dark:fill-text-table-dark" />
			)
	}
}

// Function to get object type icon
export const getObjectIcon = (type: InboxNotificationObjectType | null | undefined) => {
	if (type === null || type === undefined) {
		return null
	}

	switch (type as InboxNotificationObjectType) {
		case InboxNotificationObjectType.User:
			return (
				<People className="w-4 h-4 fill-text-table dark:fill-text-table-dark" />
			)
		case InboxNotificationObjectType.Group:
			return (
				<People className="w-4 h-4 fill-text-table dark:fill-text-table-dark" />
			)
		case InboxNotificationObjectType.Role:
			return (
				<Key className="w-4 h-4 fill-text-table dark:fill-text-table-dark" />
			)
		case InboxNotificationObjectType.File:
			return (
				<InsertDriveFile className="w-4 h-4 fill-text-table dark:fill-text-table-dark" />
			)
		case InboxNotificationObjectType.Folder:
			return (
				<InsertDriveFile className="w-4 h-4 fill-text-table dark:fill-text-table-dark" />
			)
		case InboxNotificationObjectType.TodoCard:
			return (
				<Assignment className="w-4 h-4 fill-text-table dark:fill-text-table-dark" />
			)
		default:
			return null
	}
}

// Function to map inbox messages
export const mapInboxMessage = (
	item: InboxItem
): { message: string; icon: React.ReactNode } => {
	const { template, objects, category } = item

	// Replace placeholders with objects
	// Put action objects (not users) in quotes
	let message = template || ''
	if (objects && Array.isArray(objects)) {
		objects.forEach((obj, index) => {
			// Skip null or undefined objects
			if (!obj || !obj.type) {
				message = message.replace(`{${index}}`, '')
				return
			}

			const shouldQuote = [
				InboxNotificationObjectType.File,
				InboxNotificationObjectType.Folder,
				InboxNotificationObjectType.TodoCard,
			].includes(obj.type)

			const objName = obj.name || ''
			const replacement = shouldQuote ? `'${objName}'` : objName
			message = message.replace(`{${index}}`, replacement)
		})
	}

	// Get category icon
	const categoryIcon = getCategoryIcon(category)

	return {
		message,
		icon: categoryIcon,
	}
}

// Function to format object with icon
export const formatInboxObject = (obj: any, index: number) => {
	// Safety check for null/undefined objects
	if (!obj || !obj.type) {
		return null
	}

	const icon = getObjectIcon(obj.type)
	return (
		<span
			key={obj.id || index}
			className="inline-flex items-center gap-1 text-blue-600 font-medium"
		>
			{icon}
			{obj.name || ''}
		</span>
	)
}

// Function to get readable category label
export const getCategoryLabel = (
	category: InboxNotificationCategory
): string => {
	switch (category) {
		case InboxNotificationCategory.File:
			return 'File'
		case InboxNotificationCategory.Event:
			return 'Event'
		case InboxNotificationCategory.Task:
			return 'Task'
		case InboxNotificationCategory.Access:
			return 'Access'
		case InboxNotificationCategory.Team:
			return 'Team'
		case InboxNotificationCategory.Security:
			return 'Security'
		default:
			return 'Unknown'
	}
}

// Function to get readable object type label
export const getObjectTypeLabel = (
	type: InboxNotificationObjectType
): string => {
	switch (type) {
		case InboxNotificationObjectType.User:
			return 'User'
		case InboxNotificationObjectType.Group:
			return 'Group'
		case InboxNotificationObjectType.Role:
			return 'Role'
		case InboxNotificationObjectType.File:
			return 'File'
		case InboxNotificationObjectType.Folder:
			return 'Folder'
		case InboxNotificationObjectType.TodoCard:
			return 'Task'
		default:
			return 'Unknown'
	}
}
